import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, mergeProps as _mergeProps, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "screen-wrapper" }

import ChatHeader from '@/components/common/chat-header/ChatHeader.vue';
import ChatComponent, {
  ChatComponentProps
} from '@/components/common/chat/ChatComponent.vue';
import CustomTabs from '@/components/common/tabs/CustomTabs.vue';
import CustomTab from '@/components/common/tabs/tab/CustomTab.vue';
import KeyPoints from '@/components/Reviewing/key-points/KeyPoints.vue';
import ReferenceDots from '@/components/Reviewing/reference-dots/ReferenceDots.vue';
import { ModulesEnum, SubModulesEnum } from '@/core/modulesEnum';
import { ReviewingAssistantType } from '@/models/assistant-types';
import { ChatExposedMethods } from '@/models/chat.models';
import { Conversation } from '@/models/conversation';
import { ReviewMessage } from '@/models/doc-review';
import { BaseMessage } from '@/models/messages';
import documentReviewingService from '@/services/DocumentReviewingService';
import { useAuthStore } from '@/stores/authStore';
import { useReviewingStore } from '@/stores/reviewingStore';
import { useReferenceSelectionStore } from '@/stores/useReferenceSelectionStore';
import { OfficeUtils } from '@/utils/office.utils';
import { v4 as uuidv4 } from 'uuid';
import { h, onMounted, onUnmounted, ref } from 'vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'DocumentReviewing',
  setup(__props) {

const activeTabIndex = ref<string | number>(SubModulesEnum.DOC_REVIEWING_CHAT);
const chatRef = ref<ChatExposedMethods | null>(null);
const reviewSuggestionsStore = useReviewingStore();
const authStore = useAuthStore();
const referenceStore = useReferenceSelectionStore();

onMounted(() => {
  OfficeUtils.startDocumentSelectionListener(selectionChangedHandler);
});

onUnmounted(() => {
  OfficeUtils.stopDocumentSelectionListener(selectionChangedHandler);
});

const sendMessage = async (
  message: string,
  conversation: Conversation
): Promise<{ responseMessage: BaseMessage; pickle: string }> => {
  const paragraph = await OfficeUtils.getDocumentParagraphs();
  const response = await documentReviewingService.askQuestion(
    message,
    paragraph,
    conversation
  );

  return {
    responseMessage: response.responseMessage,
    pickle: response.pickle
  };
};

const suggestionsLoader = async () => {
  if (reviewSuggestionsStore.suggestions.length)
    return reviewSuggestionsStore.suggestions;

  const paragraphs = await OfficeUtils.getDocumentParagraphs();

  const suggestions =
    await documentReviewingService.getDocumentSuggestions(paragraphs);

  reviewSuggestionsStore.setSuggestions(suggestions);
  return suggestions;
};

const reviewingChatProps: ChatComponentProps = {
  assistantType: ReviewingAssistantType.DocumentReviewingAssistant,
  onSend: sendMessage,
  botMessageFooter: (message: BaseMessage) => {
    if ('references' in message) {
      return h(ReferenceDots, {
        referencedParagraphs: (message as ReviewMessage).references,
        onResetReview,
        renderId: uuidv4()
      });
    }
    return null;
  },
  suggestionsLoader
};

const onResetReview = (resetRefs: boolean = true) => {
  if (resetRefs) reviewSuggestionsStore.setSuggestions([]);
  chatRef.value?.resetConversation();
};

const selectionChangedHandler = async (): Promise<void> => {
  try {
    const text = await OfficeUtils.getSelectedText();
    if (
      !referenceStore.selectedReference?.selectedParagraphs.every((x) =>
        text.includes(x)
      )
    )
      referenceStore.resetSelection();
  } catch (error) {
    console.error('Error getting selected text:', error);
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(ChatHeader, {
      assistant: _unref(ReviewingAssistantType).DocumentReviewingAssistant,
      "reset-conversation": () => onResetReview(false),
      "show-history": false,
      "enable-new-conversation-button": 
        String(activeTabIndex.value) === _unref(SubModulesEnum).DOC_REVIEWING_CHAT
      
    }, null, 8, ["assistant", "reset-conversation", "enable-new-conversation-button"]),
    _createVNode(CustomTabs, {
      modelValue: activeTabIndex.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((activeTabIndex).value = $event)),
      "tab-class": "tab-grow"
    }, {
      default: _withCtx(() => [
        _createVNode(CustomTab, {
          id: _unref(SubModulesEnum).DOC_REVIEWING_CHAT,
          title: _ctx.$t('reviewing.chat.tab_title'),
          disabled: 
          !_unref(authStore).products.modules[_unref(ModulesEnum).DOC_REVIEWING].includes(
            _unref(SubModulesEnum).DOC_REVIEWING_CHAT
          )
        
        }, {
          default: _withCtx(() => [
            _createVNode(ChatComponent, _mergeProps({
              ref_key: "chatRef",
              ref: chatRef
            }, reviewingChatProps, { "use-streaming-response": false }), null, 16)
          ]),
          _: 1
        }, 8, ["id", "title", "disabled"]),
        _createVNode(CustomTab, {
          id: _unref(SubModulesEnum).DOC_REVIEWING_KEYPOINTS,
          title: _ctx.$t('reviewing.key_points.tab_title'),
          disabled: 
          !_unref(authStore).products.modules[_unref(ModulesEnum).DOC_REVIEWING].includes(
            _unref(SubModulesEnum).DOC_REVIEWING_KEYPOINTS
          )
        
        }, {
          default: _withCtx(() => [
            _createVNode(KeyPoints)
          ]),
          _: 1
        }, 8, ["id", "title", "disabled"])
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})